/**
 * Data for the oxidation-number rules: which elements have a fixed state, the
 * electronegativities used as a tiebreak, and the polyatomic ions the engine recognises.
 */

export const GROUP_1 = new Set(["Li", "Na", "K", "Rb", "Cs", "Fr"]);
export const GROUP_2 = new Set(["Be", "Mg", "Ca", "Sr", "Ba", "Ra"]);

/** Halogens other than fluorine, whose −1 depends on what they are bonded to. */
export const OTHER_HALOGENS = new Set(["Cl", "Br", "I", "At"]);
export const HALOGENS = ["F", "Cl", "Br", "I", "At"];

/** Elements that are not metals. Hydrogen bonded only to metals or boron is a hydride. */
// biome-ignore format: a compact periodic table row list
export const NONMETALS = new Set([
  "H", "He", "B", "C", "N", "O", "F", "Ne", "Si", "P", "S", "Cl", "Ar",
  "Ge", "As", "Se", "Br", "Kr", "Sb", "Te", "I", "Xe", "At", "Rn", "Og",
]);

/** Electronegativity close to hydrogen's (2.20): the textbook +1 for H is a convention. */
export const NEAR_HYDROGEN = new Set(["Si", "Ge", "P", "As", "Sb", "Te", "At"]);

/** Pauling electronegativities of the main-group nonmetals, used only as a tiebreak. */
// biome-ignore format: a compact table
export const ELECTRONEGATIVITY: Record<string, number> = {
  H: 2.2, B: 2.04, C: 2.55, N: 3.04, O: 3.44, F: 3.98, Si: 1.9, P: 2.19,
  S: 2.58, Cl: 3.16, Ge: 2.01, As: 2.18, Se: 2.55, Br: 2.96, Sb: 2.05,
  Te: 2.1, I: 2.66, At: 2.2,
};

/** The usual negative state of a group 14–17 element: group number − 18. */
// biome-ignore format: a compact table
export const NEGATIVE_STATE: Record<string, number> = {
  C: -4, Si: -4, Ge: -4,
  N: -3, P: -3, As: -3, Sb: -3,
  O: -2, S: -2, Se: -2, Te: -2,
  F: -1, Cl: -1, Br: -1, I: -1, At: -1,
};

/** No element has an oxidation state above +8. */
export const MAX_STATE = 8;

export interface KnownIon {
  formula: string;
  charge: number;
}

/** Polyatomic ions and neutral ligands recognised inside a formula. */
// biome-ignore format: one ion per line
export const KNOWN_IONS: readonly KnownIon[] = [
  { formula: "OH", charge: -1 }, { formula: "CN", charge: -1 }, { formula: "SCN", charge: -1 },
  { formula: "NO3", charge: -1 }, { formula: "NO2", charge: -1 },
  { formula: "SO4", charge: -2 }, { formula: "SO3", charge: -2 }, { formula: "HSO4", charge: -1 },
  { formula: "S2O3", charge: -2 },
  { formula: "PO4", charge: -3 }, { formula: "HPO4", charge: -2 }, { formula: "H2PO4", charge: -1 },
  { formula: "AsO4", charge: -3 },
  { formula: "CO3", charge: -2 }, { formula: "HCO3", charge: -1 }, { formula: "C2O4", charge: -2 },
  { formula: "C2H3O2", charge: -1 },
  { formula: "ClO4", charge: -1 }, { formula: "ClO3", charge: -1 }, { formula: "ClO2", charge: -1 },
  { formula: "ClO", charge: -1 }, { formula: "BrO3", charge: -1 }, { formula: "IO3", charge: -1 },
  { formula: "MnO4", charge: -1 }, { formula: "CrO4", charge: -2 }, { formula: "Cr2O7", charge: -2 },
  { formula: "NH4", charge: 1 }, { formula: "NH3", charge: 0 }, { formula: "H2O", charge: 0 },
  { formula: "CO", charge: 0 },
];
